import { useNotification } from 'admin';
import {
  EvidenceSessionAIModel,
  EvidenceTypeEnum,
  getApiSamplesV1SamplesEvidenceListQueryKey,
  SampleType,
  useApiSamplesV1SamplesEvidenceListSuspense,
  useApiSamplesV1SamplesRetrieveSuspense,
  useNetworkStatus,
  type UseNetworkStatusOptions,
  useQueryClient,
} from 'api';
import HoldPhone from 'assets/camera-guide-illustrations/hold-phone.png';
import InstantFeedback from 'assets/camera-guide-illustrations/instant-feedback.png';
import PhoneAngle from 'assets/camera-guide-illustrations/phone-angle.png';
import {
  CameraDialog,
  MaterialForm,
  type OfflineProcessUploadResponse,
  type PreviewGalleryImage,
  UploadOptionsButton,
  useManagePreviews,
  useOfflineEvidence,
  useSiteMaterial,
  useToggleCamera,
} from 'features';
import { Suspense, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ActionRow,
  Box,
  Button,
  Grid,
  ListSkeletonLoader,
  NetworkOverrideSwitch,
  NetworkStatusAlert,
  Page,
  PhotoFilePicker,
  Section,
  useBreakpoint,
} from 'ui';

import { SampleDetail } from '../../components/SampleDetail/SampleDetail';
import { SiteHeader } from '../../components/SiteHeader/SiteHeader';

export const Sample = () => {
  const { sampleId } = useParams();
  const queryClient = useQueryClient();
  const { data: siteMaterial } = useSiteMaterial();
  const { data: sample } = useApiSamplesV1SamplesRetrieveSuspense(Number(sampleId));
  const { data: evidence } = useApiSamplesV1SamplesEvidenceListSuspense(Number(sampleId), {
    page_size: 10000,
  });
  const { isCameraOpen, toggle } = useToggleCamera();
  const { isMobile } = useBreakpoint();
  const { open } = useNotification();
  const filePickerRef = useRef<HTMLInputElement>(null);

  const [networkOverrideMode, setNetworkOverrideMode] = useState<UseNetworkStatusOptions['mode']>('auto');
  const { isOnline } = useNetworkStatus({ mode: networkOverrideMode });

  const evidenceType = sample.type === SampleType.AG ? EvidenceTypeEnum.AS : EvidenceTypeEnum.SB;

  const { mutateAsync: uploadEvidence, removeEvidenceFile } = useOfflineEvidence({
    isOnline,
    mode: 'sample',
    mutationOptions: {
      onError: (error, { photo }) => {
        open?.({
          message: `Failed to upload ${photo.name}: ${error}`,
          type: 'error',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getApiSamplesV1SamplesEvidenceListQueryKey(Number(sampleId), { page_size: 10000 }),
        });
      },
    },
    parentId: Number(sampleId),
  });

  const {
    previewImages,
    addImage,
    updateOfflineImage,
    updateOnlineImage,
    resetSelectedImages,
    handleDeletePreviewImage,
  } = useManagePreviews({
    onDeleteSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getApiSamplesV1SamplesEvidenceListQueryKey(Number(sampleId), { page_size: 10000 }),
      });
    },
  });

  const handleCapture = async (photo: File) => {
    const addedImage = await addImage({
      isOnline,
      photo,
    });

    const result = await uploadEvidence({
      evidenceType,
      parentId: sampleId ?? '',
      photo,
      startTime: Date.now(),
    });

    if ('model_results' in result && result.model_results) {
      updateOnlineImage({
        accepted: true,
        aiModel: (result.model_results?.model_name as EvidenceSessionAIModel) ?? undefined,
        annotations: [...(result.model_results?.coco_prediction ?? [])],
        attachmentDetails: result.attachment_details ?? undefined,
        imageRejectionReasons: [],
        previewImage: addedImage,
        targetWeightPercentage: result.target_weight_percentage ?? undefined,
        url: typeof result.image === 'string' ? result.image : result.image.url,
      });
    } else {
      updateOfflineImage({
        id: (result as OfflineProcessUploadResponse).id,
        previewImage: addedImage,
      });
    }
  };

  const handleFileSelect = () => {
    filePickerRef.current?.click();
  };

  const deletePreviewImage = (image: PreviewGalleryImage) => {
    handleDeletePreviewImage({
      evidence: evidence?.results || [],
      image,
      isOnline,
      removeEvidenceFile,
    });
  };

  const handleCompletePreviewSession = () => {
    resetSelectedImages();
    toggle();
  };

  return (
    <>
      <Page>
        <Page.Title title="Manage Sample" />
        <Page.Divider />
        <Grid container>
          <Grid item xs={12} md={4}>
            <Section title="Site Details">
              <SiteHeader siteMaterial={siteMaterial} />
            </Section>
          </Grid>
          <Grid item xs={12} md={8}>
            <Section
              title="Sample details"
              action={
                <ActionRow>
                  <NetworkOverrideSwitch
                    checked={networkOverrideMode === 'auto'}
                    currentlyOnline={isOnline}
                    onChange={(checked) => setNetworkOverrideMode(checked ? 'auto' : 'offline')}
                  />
                  <Box display="flex" gap={2} flexDirection={isMobile ? 'column' : 'row'}>
                    <Button size="large" fullWidth variant="secondary" component={Link} to="edit">
                      Edit
                    </Button>
                    <UploadOptionsButton onUpload={handleFileSelect} onOpenCamera={toggle} isMobile={isMobile} />
                  </Box>
                </ActionRow>
              }
            >
              <NetworkStatusAlert isOnline={isOnline} />
              <Suspense fallback={<ListSkeletonLoader />}>
                <SampleDetail
                  sampleId={Number(sampleId)}
                  evidence={evidence}
                  isOnline={isOnline}
                  onSuccessfulEvidenceSync={() => {
                    queryClient.invalidateQueries({
                      queryKey: getApiSamplesV1SamplesEvidenceListQueryKey(Number(sampleId), { page_size: 10000 }),
                    });
                  }}
                />
              </Suspense>
            </Section>
          </Grid>
        </Grid>
      </Page>

      <CameraDialog
        isCameraOpen={isCameraOpen}
        toggle={toggle}
        cameraProps={{
          guideIllustrations: {
            holdPhone: HoldPhone,
            instantFeedback: InstantFeedback,
            phoneAngle: PhoneAngle,
          },
          handleCapture,
          handleClose: toggle,
          handleDeletePreviewImage: deletePreviewImage,
          handleOnComplete: handleCompletePreviewSession,
          materialForm: evidenceType === EvidenceTypeEnum.AS ? MaterialForm.loose : MaterialForm.baled,
          previewImages,
        }}
      />

      <PhotoFilePicker
        ref={filePickerRef}
        onSelectFiles={(photos) => photos.forEach((photo) => handleCapture(photo))}
        hide
        multiple
      />
    </>
  );
};
